import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStore } from '../context/StoreContext';

const { FiX, FiTag, FiPlus, FiCheck } = FiIcons;

const TransactionModal = ({ isOpen, onClose, onSave, initialData, properties }) => {
  const { data, updateData } = useStore();
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState({
    propertyId: '',
    date: '',
    type: 'Expense',
    category: '',
    amount: '',
    description: '',
    tags: []
  });

  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        propertyId: '',
        date: new Date().toISOString().split('T')[0],
        type: 'Expense',
        category: data.expenseCategories[0] || '',
        amount: '',
        description: '',
        tags: []
      });
    }
  }, [initialData, isOpen, data]);

  const handleTypeChange = (newType) => {
    const defaultCat = newType === 'Income' ? data.incomeCategories[0] : data.expenseCategories[0];
    setFormData({ ...formData, type: newType, category: defaultCat });
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) 
        ? prev.tags.filter(t => t !== tag) 
        : [...prev.tags, tag]
    }));
  };

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, amount: Number(formData.amount) });
    onClose();
  };

  const currentCategories = formData.type === 'Income' ? data.incomeCategories : data.expenseCategories;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-200">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
              <SafeIcon icon={FiTag} />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900">{initialData?.id ? 'Edit Entry' : 'Record Entry'}</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Financial Audit System</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <SafeIcon icon={FiX} size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Entry Type</label>
            <div className="flex bg-gray-100 p-1 rounded-2xl">
              <button 
                type="button" 
                className={`flex-1 py-3 text-xs font-black uppercase tracking-tight rounded-xl transition-all ${formData.type === 'Income' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-500'}`}
                onClick={() => handleTypeChange('Income')}
              >
                Revenue
              </button>
              <button 
                type="button" 
                className={`flex-1 py-3 text-xs font-black uppercase tracking-tight rounded-xl transition-all ${formData.type === 'Expense' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500'}`}
                onClick={() => handleTypeChange('Expense')}
              >
                Expenditure
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Property</label>
              <select 
                required
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.propertyId}
                onChange={e => setFormData({ ...formData, propertyId: e.target.value })}
              >
                <option value="">Select Target...</option>
                <option value="shared">General / Portfolio</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Amount ($)</label>
              <input 
                required 
                type="number" 
                step="0.01"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.amount}
                onChange={e => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Category</label>
              {showAddCategory ? (
                <div className="flex gap-2">
                  <input
                    autoFocus
                    value={newCategoryName}
                    onChange={e => setNewCategoryName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const name = newCategoryName.trim();
                        if (name) {
                          const key = formData.type === 'Income' ? 'incomeCategories' : 'expenseCategories';
                          if (!data[key].includes(name)) {
                            updateData(key, [...data[key], name]);
                          }
                          setFormData(prev => ({ ...prev, category: name }));
                          setNewCategoryName('');
                          setShowAddCategory(false);
                        }
                      }
                      if (e.key === 'Escape') { setShowAddCategory(false); setNewCategoryName(''); }
                    }}
                    className="flex-1 p-3 bg-gray-50 border border-indigo-300 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="New category name..."
                  />
                  <button type="button" onClick={() => {
                    const name = newCategoryName.trim();
                    if (name) {
                      const key = formData.type === 'Income' ? 'incomeCategories' : 'expenseCategories';
                      if (!data[key].includes(name)) {
                        updateData(key, [...data[key], name]);
                      }
                      setFormData(prev => ({ ...prev, category: name }));
                      setNewCategoryName('');
                      setShowAddCategory(false);
                    }
                  }} className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors">
                    <SafeIcon icon={FiCheck} size={16} />
                  </button>
                  <button type="button" onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}
                    className="p-3 bg-gray-100 text-gray-500 rounded-xl hover:bg-gray-200 transition-colors">
                    <SafeIcon icon={FiX} size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <select
                    required
                    className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                    value={formData.category}
                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                  >
                    {currentCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => setShowAddCategory(true)}
                    title="Add new category"
                    className="p-3 bg-gray-50 border border-gray-200 text-gray-400 rounded-xl hover:border-indigo-300 hover:text-indigo-600 transition-colors">
                    <SafeIcon icon={FiPlus} size={16} />
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Date</label>
              <input
                required
                type="date"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.date}
                onChange={e => setFormData({ ...formData, date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Tags / Labels</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {(data.transactionTags || []).map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all border ${
                    formData.tags.includes(tag)
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-400 border-gray-100 hover:border-indigo-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Memo / Notes</label>
            <textarea 
              rows="2"
              className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detailed explanation of the entry..."
            />
          </div>
        </form>

        <div className="p-6 border-t border-gray-100 bg-white flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-3 font-bold text-gray-400 hover:text-gray-600 transition">
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            className="px-10 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 flex items-center gap-2"
          >
            <SafeIcon icon={FiCheck} /> {initialData?.id ? 'Update Entry' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;